/**
 * Templates Tab Component
 * Displays public slide templates from /mnt/filestore/public/slide_templates/
 */

import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Presentation, Loader2, AlertCircle } from 'lucide-react';
import { templateAPI, conversationAPI } from '@/services/api';
import { TemplateCard } from '../chat/TemplateCard';
import slidePathMapping from '@/services/slideMapping';

interface TemplatesTabProps {
  conversationId: string;
}

interface TemplateData {
  id: string;
  name: string;
  path: string;
  relativePath: string;
  slideJsonUrl: string;
  previewUrl?: string;
  metadata?: any;
}

export const TemplatesTab: React.FC<TemplatesTabProps> = ({ conversationId }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appliedTemplateId, setAppliedTemplateId] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
    fetchAppliedTemplate();
  }, []);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await templateAPI.listPublicTemplates();

      if (response.success) {
        // Flatten hierarchical structure to flat list for compatibility
        const flatTemplates: TemplateData[] = [];

        for (const item of response.templates) {
          if (item.type === 'parent') {
            // Add all child layouts from parent template
            for (const layout of item.layouts) {
              flatTemplates.push({
                id: layout.id,
                name: layout.name,
                path: layout.slideJsonUrl.replace('/api/public/slide_templates/', '').replace('/slide.json', ''),
                relativePath: layout.id,
                slideJsonUrl: layout.slideJsonUrl,
                previewUrl: layout.previewUrl,
                metadata: layout.metadata,
              });
            }
          } else {
            // Add standalone layout
            flatTemplates.push({
              id: item.id,
              name: item.name,
              path: item.slideJsonUrl.replace('/api/public/slide_templates/', '').replace('/slide.json', ''),
              relativePath: item.id,
              slideJsonUrl: item.slideJsonUrl,
              previewUrl: item.previewUrl,
              metadata: item.metadata,
            });
          }
        }

        setTemplates(flatTemplates);
      } else {
        setError('Failed to load templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAppliedTemplate = async () => {
    try {
      const response = await conversationAPI.getById(conversationId);
      const applied = response.conversation.metadata?.appliedTemplateId || null;
      setAppliedTemplateId(applied);
      console.log('📋 Loaded applied template:', applied);
    } catch (error) {
      console.error('Failed to fetch applied template:', error);
    }
  };

  const handleTemplateClick = async (template: TemplateData) => {
    // Generate hash from template path
    const slideHash = await slidePathMapping.generateHashForPath(template.relativePath);

    // Navigate to slide preview with template context
    navigate({
      to: '/chat/$conversationId/slide/$slideHash',
      params: {
        conversationId,
        slideHash
      },
      search: {
        fromTab: 'templates'
      } as any // Type cast to avoid search param type conflicts
    });
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-background p-6">
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <div>
            <h3 className="font-semibold text-lg mb-1">Failed to Load Templates</h3>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
          <button
            onClick={fetchTemplates}
            className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-background p-6">
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <Presentation className="h-12 w-12 text-muted-foreground" />
          <div>
            <h3 className="font-semibold text-lg mb-1">No Templates Available</h3>
            <p className="text-sm text-muted-foreground">
              No slide templates are currently available in the library.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b bg-card/50">
        <div className="flex items-center gap-3">
          <Presentation className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">Slide Templates</h2>
            <p className="text-sm text-muted-foreground">
              {templates.length} {templates.length === 1 ? 'template' : 'templates'} available
            </p>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
          {templates.map((template, index) => (
            <TemplateCard
              key={template.id}
              template={template}
              isSelected={template.id === appliedTemplateId}
              onTemplateClick={handleTemplateClick}
              index={index}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
